import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { invalidateAllCaches } from '../cacheInvalidation';
import { useAuth } from '@/contexts/AuthContext';
import type { UserSettings, UpdateUserSettingsData, CurrenciesResponse } from '../types';

export function useUserSettings() {
    return useQuery({
        queryKey: ['user-settings'],
        queryFn: () => api.get<UserSettings>('/api/user-settings'),
    });
}

/**
 * Supported currencies and their precision. Static reference data, so it is fetched
 * once per session and shared by every consumer through this single query key.
 */
export function useSupportedCurrencies(enabled = true) {
    return useQuery({
        queryKey: ['supported-currencies'],
        queryFn: () => api.get<CurrenciesResponse>('/api/currencies/supported'),
        enabled,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: 3,
    });
}

export function useUpdateUserSettings() {
    const queryClient = useQueryClient();
    const { user, updateFromBatch } = useAuth();

    return useMutation({
        mutationFn: (data: UpdateUserSettingsData) =>
            api.patch<UserSettings>('/api/user-settings', data),
        onSuccess: (updatedSettings) => {
            // Update AuthContext immediately for reactive UI updates
            if (user) {
                updateFromBatch(user, updatedSettings);
            }

            // Invalidate all caches to ensure fresh data with new settings
            invalidateAllCaches(queryClient);
        },
    });
}
